// src/components/common/AccessRequestsPanel.jsx

import React, { useState } from 'react';
import { useAuth }          from '../../contexts/AuthContext';
import { useAccessRequests } from '../../hooks/useAccessRequests';


import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';

import {
  Box, Typography, Button, Alert, Paper, Chip,
  Drawer, TextField, MenuItem, FormControl, InputLabel,
  Select, Snackbar, Divider, Stack, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, Skeleton,
} from '@mui/material';

import {
  Check      as ApproveIcon,
  Close      as RejectIcon,
  Undo       as UndoIcon,
  Info       as InfoIcon,
  Warning    as WarningIcon,
} from '@mui/icons-material';



// ─── Pure helpers outside component ──────────────────────────────────────────

const statusColor = (s) =>
  s === 'approved' ? 'success' : s === 'rejected' ? 'error' : 'info';

/**
 * Safely formats a timestamp that might be:
 *   - a Firestore Timestamp: { seconds, nanoseconds }
 *   - an ISO string:         "2024-05-01T12:00:00.000Z"
 *   - a JS Date
 *   - a number (ms since epoch)
 *   - null / undefined / anything else
 */
const formatTimestamp = (value) => {
  if (!value) return 'N/A';

  // Firestore Timestamp shape
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000).toLocaleString();
  }

  // ISO string, number, or Date
  const d = new Date(value);
  return isNaN(d.getTime()) ? 'N/A' : d.toLocaleString();
};

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarQuickFilter sx={{ mr: 2 }} />
      <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
    </GridToolbarContainer>
  );
}

// ─── Skeleton for loading state ───────────────────────────────────────────────

const AccessRequestsSkeleton = () => (
  <Box sx={{ p: { xs: 1, md: 3 } }}>
    <Skeleton variant="text"    width={220} height={44} sx={{ mb: 2 }} />
    <Skeleton variant="rounded" width="100%" height={40} sx={{ mb: 2 }} />
    <Box sx={{
      background: '#fff', borderRadius: '12px',
      padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    }}>
      {/* Table header */}
      <Box sx={{ display: 'flex', gap: 2, py: 1.5, mb: 1, borderBottom: '2px solid #e2e8f0' }}>
        {[120, 160, 100, 100, 120, 80, 100, 160, 100, 90].map((w, i) => (
          <Skeleton key={i} variant="text" width={w} height={16} />
        ))}
      </Box>
      {/* Table rows */}
      {Array.from({ length: 6 }, (_, i) => (
        <Box key={i} sx={{
          display: 'flex', gap: 2, py: 1.5,
          borderBottom: '1px solid #f1f5f9', alignItems: 'center',
        }}>
          <Skeleton variant="text"    width={120} height={14} />
          <Skeleton variant="text"    width={160} height={14} />
          <Skeleton variant="text"    width={100} height={14} />
          <Skeleton variant="text"    width={100} height={14} />
          <Skeleton variant="text"    width={120} height={14} />
          <Skeleton variant="rounded" width={70}  height={24} />
          <Skeleton variant="text"    width={100} height={14} />
          <Skeleton variant="text"    width={160} height={14} />
          <Skeleton variant="text"    width={140} height={14} />
          <Skeleton variant="rounded" width={80}  height={24} />
        </Box>
      ))}
    </Box>
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const AccessRequestsPanel = () => {
  const { currentUser } = useAuth();

  // ✅ All data logic in hook → service → repo
  const {
    userRequests, adminRequests,
    loading, fetchError, notification, processing, lastAction,
    approveRequest, rejectRequest, undoLastAction,
    generateRandomPassword,
    setFetchError, setNotification, setLastAction,
  } = useAccessRequests();

  // ── Local UI state only ──────────────────────────────────────────────────
  const [drawerOpen,    setDrawerOpen]    = useState(false);
  const [selectedRow,   setSelectedRow]   = useState(null);
  const [selectedRole,  setSelectedRole]  = useState('user');
  const [password,      setPassword]      = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [activeTab,     setActiveTab]     = useState(0);

  // Email conflict dialog
  const [emailConflictDialog,  setEmailConflictDialog]  = useState(false);
  const [conflictingRequest,   setConflictingRequest]   = useState(null);

  // Rejection dialog
  const [rejectionDialog,      setRejectionDialog]      = useState(false);
  const [rejectionReason,      setRejectionReason]      = useState('');
  const [rejectionReasonError, setRejectionReasonError] = useState('');

  // ── Role helpers ─────────────────────────────────────────────────────────
  const getAllowedRoles = () => {
    if (currentUser?.role === 'superadmin') {
      return [
        { value: 'user',       label: 'User'        },
        { value: 'admin',      label: 'Admin'       },
        { value: 'superadmin', label: 'Super Admin' },
      ];
    }
    if (currentUser?.role === 'admin') {
      return [
        { value: 'user',  label: 'User'  },
        { value: 'admin', label: 'Admin' },
      ];
    }
    return [{ value: 'user', label: 'User' }];
  };

  // ── Approve handler ──────────────────────────────────────────────────────
  const handleApprove = async () => {
    if (!password) {
      setPasswordError('Password is required');
      return;
    }

    const allowedRoles = getAllowedRoles().map(r => r.value);
    if (!allowedRoles.includes(selectedRole)) {
      setFetchError(`You don't have permission to assign role: ${selectedRole}`);
      return;
    }

    // ✅ Calls hook → service → repo
    const result = await approveRequest(selectedRow, selectedRole, password);

    if (result.emailConflict) {
      setConflictingRequest(selectedRow);
      setEmailConflictDialog(true);
      return;
    }

    if (result.success) {
      setDrawerOpen(false);
      setSelectedRow(null);
      setPassword('');
    }
  };

  // ── Reject handlers ──────────────────────────────────────────────────────
  const handleRejectClick = () => {
    if (!selectedRow) return;
    setRejectionDialog(true);
    setRejectionReason('');
    setRejectionReasonError('');
  };

  const handleConfirmReject = async () => {
    if (!rejectionReason.trim()) {
      setRejectionReasonError('Please provide a reason for rejection');
      return;
    }

    // ✅ Calls hook → service → repo
    const result = await rejectRequest(selectedRow, rejectionReason.trim());

    if (result.success) {
      setDrawerOpen(false);
      setRejectionDialog(false);
      setSelectedRow(null);
      setRejectionReason('');
    } else {
      setFetchError(result.error);
    }
  };

  // ── Table columns ────────────────────────────────────────────────────────
  const columns = [
    { field: 'name',       headerName: 'Name',       width: 140 },
    { field: 'email',      headerName: 'Email',      width: 180 },
    { field: 'company',    headerName: 'Company',    width: 120 },
    { field: 'companyId',  headerName: 'Company ID', width: 100 },
    {
      field: 'departmentName',
      headerName: 'Department',
      width: 130,
      renderCell: (p) => (
        <span style={{ color: p.value === 'Not specified' ? '#999' : 'inherit' }}>
          {p.value || 'Not specified'}
        </span>
      ),
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 105,
      renderCell: (p) => (
        <Chip
          label={p.value || 'user'}
          size="small"
          color={p.value === 'admin' ? 'secondary' : 'primary'}
        />
      ),
    },
    { field: 'location',    headerName: 'Location',    width: 110 },
    { field: 'description', headerName: 'Justification', width: 180 },
    {
      field: 'createdAt',
      headerName: 'Requested On',
      width: 180,
      // ❌ No `type: 'dateTime'` — Firestore Timestamps are not JS Dates,
      // and MUI tries (and fails) to coerce them when the type is set.
      valueFormatter: (value) => formatTimestamp(value),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 105,
      renderCell: (p) => (
        <Chip label={p.value || 'pending'} size="small" color={statusColor(p.value)} />
      ),
    },
  ];

  const isSuper = currentUser?.role === 'superadmin';
  const data    = isSuper
    ? (activeTab === 0 ? adminRequests : userRequests)
    : userRequests;

  // ── Guards ───────────────────────────────────────────────────────────────
  if (!currentUser || !['admin', 'superadmin'].includes(currentUser.role)) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          You do not have permission to view this page.
        </Typography>
      </Box>
    );
  }

  // ✅ MUI Skeleton instead of CircularProgress
  if (loading) return <AccessRequestsSkeleton />;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Typography variant="h4" gutterBottom fontWeight={700}>
        Access Requests
      </Typography>

      {currentUser.role === 'admin' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <b>Viewing requests for:</b>{' '}
          {currentUser.company || 'Unassigned'} (ID: {currentUser.companyId || 'N/A'})
        </Alert>
      )}

      {isSuper && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
            <Tab label={`Admin Requests (${adminRequests.length})`} />
            <Tab label={`User Requests (${userRequests.length})`}  />
          </Tabs>
        </Box>
      )}

      {/* Undo snackbar */}
      {lastAction && Date.now() - lastAction.time < 10_000 && (
        <Snackbar open message={
          <>
            {lastAction.type === 'approved' ? 'Approved.' : 'Rejected.'}{' '}
            <Button size="small" startIcon={<UndoIcon />}
              onClick={undoLastAction} disabled={processing}>
              Undo
            </Button>
          </>
        } />
      )}

      {/* Notification snackbar */}
      {notification && (
        <Snackbar open autoHideDuration={5000}
          onClose={() => setNotification('')}
          message={notification} />
      )}

      {fetchError && (
        <Alert severity="error" sx={{ my: 2 }}>{fetchError}</Alert>
      )}

      {data.length === 0 ? (
        <Alert severity="warning">
          <InfoIcon sx={{ mr: 0.5 }} />
          No pending {isSuper ? (activeTab === 0 ? 'admin' : 'user') : 'user'} requests.
        </Alert>
      ) : (
        <Paper sx={{ height: 600, mt: 2, borderRadius: 3, boxShadow: 2 }}>
          <DataGrid
            rows={data}
            columns={columns}
            pageSizeOptions={[5, 10, 25]}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            slots={{ toolbar: CustomToolbar }}
            onRowClick={({ row }) => {
              setSelectedRow(row);
              setDrawerOpen(true);
              setPassword('');
              setPasswordError('');
              const allowed = getAllowedRoles().map(r => r.value);
              setSelectedRole(allowed.includes(row.role) ? row.role : 'user');
            }}
            disableRowSelectionOnClick
          />
        </Paper>
      )}

      {/* Email conflict dialog */}
      <Dialog open={emailConflictDialog} onClose={() => setEmailConflictDialog(false)}>
        <DialogTitle>
          <WarningIcon color="warning" sx={{ mr: 1 }} />
          Email Already Exists
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            The email <b>{conflictingRequest?.email}</b> is already registered.
            Please reject this request instead.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailConflictDialog(false)}>Close</Button>
          <Button color="error" variant="contained"
            onClick={() => {
              setEmailConflictDialog(false);
              rejectRequest(conflictingRequest, 'email already exists');
            }}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection reason dialog */}
      <Dialog open={rejectionDialog} onClose={() => setRejectionDialog(false)}
        maxWidth="sm" fullWidth>
        <DialogTitle>
          <RejectIcon color="error" sx={{ mr: 1 }} />
          Reject Access Request
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Please provide a reason for rejecting <b>{selectedRow?.name}'s</b> request.
            This will be sent to the user via email.
          </DialogContentText>
          <TextField autoFocus fullWidth multiline rows={4}
            label="Rejection Reason"
            placeholder="e.g., Invalid company information, insufficient authorization..."
            value={rejectionReason}
            onChange={e => { setRejectionReason(e.target.value); setRejectionReasonError(''); }}
            error={!!rejectionReasonError}
            helperText={rejectionReasonError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectionDialog(false)}>Cancel</Button>
          <Button color="error" variant="contained"
            onClick={handleConfirmReject} disabled={processing}>
            {processing ? 'Rejecting...' : 'Reject & Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail drawer */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}
        sx={{ '& .MuiDrawer-paper': { width: { xs: '95vw', sm: 420 }, p: 3 } }}>
        {selectedRow && (
          <>
            <Typography variant="h6" fontWeight={700} mb={2}>
              <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} color="info" />
              {selectedRow.requestType === 'admin' ? 'Admin' : 'User'} Request
            </Typography>

            <Stack spacing={1}>
              <Divider />
              <Typography><b>Name:</b>     {selectedRow.name}</Typography>
              <Typography><b>Email:</b>    {selectedRow.email}</Typography>
              <Typography component="div">
                <b>Requested Role:</b>{' '}
                <Chip size="small" label={selectedRow.role || 'user'}
                  color={selectedRow.role === 'admin' ? 'secondary' : 'primary'} />
              </Typography>
              <Typography><b>Company:</b>    {selectedRow.company}</Typography>
              <Typography><b>Company ID:</b> {selectedRow.companyId || 'Not assigned'}</Typography>
              <Typography>
                <b>Department:</b>{' '}
                <span style={{
                  color: (!selectedRow.departmentName || selectedRow.departmentName === 'Not specified')
                    ? '#999' : 'inherit',
                  fontStyle: (!selectedRow.departmentName || selectedRow.departmentName === 'Not specified')
                    ? 'italic' : 'normal',
                }}>
                  {selectedRow.departmentName || 'Not specified'}
                </span>
              </Typography>
            
              
              <Typography><b>Location:</b>     {selectedRow.location}</Typography>
              <Typography><b>Justification:</b></Typography>
              <Typography variant="body2" color="text.secondary">
                "{selectedRow.description}"
              </Typography>
              <Typography>
                <b>Requested At:</b>{' '}
                {formatTimestamp(selectedRow.createdAt)}
              </Typography>
              <Divider sx={{ my: 1 }} />
            </Stack>

            {selectedRow.status === 'pending' ? (
              <>
                <Typography variant="subtitle1" fontWeight={600} mt={2}>
                  Admin Actions
                  <Chip size="small" sx={{ ml: 1 }}
                    label={currentUser.role === 'superadmin' ? 'Super Admin' : 'Admin'}
                    color={currentUser.role === 'superadmin' ? 'error' : 'secondary'} />
                </Typography>

                {selectedRow.requestType !== 'admin' && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <b>Note:</b> This user will be assigned to their requested company:{' '}
                    <b>{selectedRow.company}</b> (ID: {selectedRow.companyId})
                  </Alert>
                )}

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel>Assign Role</InputLabel>
                  <Select value={selectedRole} label="Assign Role"
                    onChange={e => setSelectedRole(e.target.value)}>
                    {getAllowedRoles().map(role => (
                      <MenuItem key={role.value} value={role.value}>
                        {role.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {selectedRole !== selectedRow.role && (
                    <Typography variant="caption" color="warning.main" sx={{ mt: 1 }}>
                      ⚠️ Role will change from "{selectedRow.role}" to "{selectedRole}"
                    </Typography>
                  )}
                </FormControl>

                <TextField label="Initial Password" fullWidth sx={{ mt: 2 }}
                  value={password}
                  onChange={e => { setPassword(e.target.value); setPasswordError(''); }}
                  error={!!passwordError}
                  helperText={passwordError || 'Set a strong password or generate one'}
                />
                <Button size="small" variant="outlined" sx={{ mt: 1 }}
                  onClick={() => setPassword(generateRandomPassword())}>
                  Generate Password
                </Button>

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" spacing={2}>
                  <Button variant="contained" color="success"
                    startIcon={<ApproveIcon />}
                    onClick={handleApprove} disabled={processing}>
                    {processing ? 'Processing...' : 'Approve'}
                  </Button>
                  <Button variant="contained" color="error"
                    startIcon={<RejectIcon />}
                    onClick={handleRejectClick} disabled={processing}>
                    Reject
                  </Button>
                </Stack>

                {fetchError && (
                  <Alert severity="error" sx={{ mt: 2 }}>{fetchError}</Alert>
                )}
              </>
            ) : (
              <Alert severity={selectedRow.status === 'approved' ? 'success' : 'error'} sx={{ mt: 2 }}>
                This request has already been <b>{selectedRow.status}</b>.
              </Alert>
            )}
          </>
        )}
      </Drawer>
    </Box>
  );
};

export default AccessRequestsPanel;